import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { CHECKOUT_PROBABILITY_LINE_ITEM_PROPERTY_KEY } from "@redotech/redo-model/checkouts/segments";
import { AllCookies, getCookie } from "@redotech/redo-web/utils/cookies";
import { STORE_URL_HEADER } from "@redotech/shopify-sdk/rpc/custom-http-headers";
import { getSessionHazardProbabilities } from "@redotech/shopify-sdk/rpc/schema/checkout-optimization/get-session-hazard-probabilities";
import { memo, useContext, useEffect, useRef } from "react";
import { RedoShopifyRpcClientContext } from "../../client/redo-shopify-rpc-client";
import { log } from "../../monitoring/log";
import { ExtensionShopifyClientContext } from "../../shopify";

const STALE_PAGE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

export const CheckoutProbabilityTracker = memo(
  function CheckoutProbabilityTracker() {
    const shopifyRpcClient = useContext(RedoShopifyRpcClientContext);
    const extensionShopifyClient = useRequiredContext(
      ExtensionShopifyClientContext,
    );

    const intervalRef = useRef<NodeJS.Timeout>();
    const lastProbabilityRef = useRef<number | null>(null);
    const pageLoadTimeRef = useRef<number>(Date.now());

    const updateCheckoutProbability = useHandler(async () => {
      try {
        // Check if page has been stale for more than 10 minutes
        const timeSincePageLoad = Date.now() - pageLoadTimeRef.current;
        if (timeSincePageLoad > STALE_PAGE_TIMEOUT_MS) {
          log(
            `Page has been stale for ${Math.round(timeSincePageLoad / 1000 / 60)} minutes, skipping checkout probability update`,
          );
          return;
        }

        const shopifySessionId = getCookie(AllCookies.ShopifySessionId);
        if (!shopifySessionId) {
          log(
            "Shopify session ID not found, skipping checkout probability update",
          );
          return;
        }

        if (!shopifyRpcClient) {
          console.warn("Shopify RPC client not available");
          return;
        }

        const storeUrl =
          globalThis.redoStorefront?.storeUrl ?? globalThis.Shopify?.shop;
        if (!storeUrl) {
          console.warn("Store URL not available");
          return;
        }

        if (!extensionShopifyClient.updateLineItemAttributes) {
          console.warn("updateLineItemAttributes is not available");
          return;
        }

        // Get the current checkout probability from RPC endpoint
        const result = await shopifyRpcClient(
          getSessionHazardProbabilities,
          { sessionId: shopifySessionId },
          { method: "GET", requestHeaders: { [STORE_URL_HEADER]: storeUrl } },
        );

        if (!result?.cumulativeConversionProbability) {
          console.warn(
            "No cumulative conversion probability returned from RPC",
          );
          return;
        }

        const probabilityScore = Math.round(
          result.cumulativeConversionProbability * 100,
        );
        log("probabilityScore", probabilityScore);

        // Only update if the probability has changed
        if (lastProbabilityRef.current === probabilityScore) {
          log("probabilityScore is the same, skipping");
          return;
        }

        // Get current cart
        const cart = await extensionShopifyClient.cartGet();
        if (!cart?.items || cart.items.length === 0) {
          return;
        }

        // Update all line items with the new probability score in a single batch
        const updates = cart.items.map((lineItem) => {
          const existingProperties = lineItem.properties ?? {};
          return {
            lineItemId: String(lineItem.id),
            attributes: [
              ...Object.entries(existingProperties).map(([key, value]) => ({
                key,
                value,
              })),
              {
                key: CHECKOUT_PROBABILITY_LINE_ITEM_PROPERTY_KEY,
                value: probabilityScore.toString(),
              },
            ],
          };
        });

        await extensionShopifyClient.updateLineItemAttributes?.(updates);

        lastProbabilityRef.current = probabilityScore;
      } catch (error) {
        console.error("Error in checkout probability tracker:", error);
      }
    });

    useEffect(() => {
      // Initial update
      void updateCheckoutProbability();

      // Set up interval to update every 15 seconds
      intervalRef.current = setInterval(updateCheckoutProbability, 15_000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }, [updateCheckoutProbability]);

    // This component doesn't render anything visible
    return null;
  },
);
